import { useState, useEffect, useCallback } from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { CommerceList } from '@msdyn365-commerce/retail-proxy';
import {
    addLinesAsync,
    createCommerceListAsync,
    createGetByCustomerInput,
    getByCustomerAsync,
    removeLinesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { buildWishlistLine } from '@msdyn365-commerce-modules/retail-actions';
import { useProductEngagementTracker } from '../../integrations/relewise/hooks/useProductEngagementTracker';
import { useDatalayerService } from '../../integrations/datalayer/hooks/useDatalayerService';

const WISHLIST_COMMERCE_LIST_TYPE_VALUE = 1;
const NEW_COMMERCE_LIST_ID = 0;
const PENDING_WISHLIST_ITEM_KEY = 'pending-wishlist-item';
const WISHLIST_NAME = 'Wishlist';

export interface WishlistItemArgs {
    concatenatedId: string | undefined;
    itemId: string | undefined;
    variantRecordId: number | undefined;
    relewiseVariantId: string | undefined;
    relewiseProductId: string | undefined;
    variantDisplayName: string | undefined;
    discount: number | undefined;
    brand: string | undefined;
    color: string | undefined;
    price: number | undefined;
    size: string | undefined;
}

interface UseWishlistResult {
    isProductInWishlist: (variantRecordId: number | undefined) => boolean;
    isLoading: boolean;
    addToWishlistError: boolean;
    toggleWishlist: (args: WishlistItemArgs) => Promise<void>;
    isWishlistReady: boolean;
}

export const useWishlist = (context: ICoreContext, location: string): UseWishlistResult => {
    const [wishlist, setWishlist] = useState<CommerceList | undefined>();
    const [isWishlistReady, setIsWishlistReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [addToWishlistError, setAddToWishlistError] = useState(false);
    const { trackProductEngagement } = useProductEngagementTracker(context.request, context.telemetry);
    const datalayerService = useDatalayerService({ request: context.request, telemetry: context.telemetry });

    const logWishlistError = useCallback(
        (message: string) => {
            const fullMessage = `[${location}] ${message}`;
            console.error(fullMessage);
            context.telemetry.error(fullMessage);
        },
        [context.telemetry, location]
    );

    useEffect(() => {
        if (typeof window === 'undefined' || !context.request.user.isAuthenticated) {
            setIsWishlistReady(true);
            return;
        }

        getByCustomerAsync({ callerContext: context.actionContext, queryResultSettings: {} }, null)
            .then(result => {
                setWishlist(result?.[0]);
                setIsWishlistReady(true);
            })
            .catch(error => {
                logWishlistError(`Failed to fetch wishlist: ${error instanceof Error ? error.message : String(error)}`);
            });
    }, [context.actionContext, context.request.user.isAuthenticated, logWishlistError]);

    const isProductInWishlist = useCallback(
        (variantRecordId: number | undefined): boolean => {
            if (variantRecordId === undefined) {
                return false;
            }
            return wishlist?.CommerceListLines?.some(line => line.ProductId === variantRecordId) ?? false;
        },
        [wishlist]
    );

    const addToWishlist = useCallback(
        async (args: WishlistItemArgs) => {
            if (args.variantRecordId === undefined) {
                return;
            }
            const customerAccountNumber = context.request.user.customerAccountNumber;
            if (!customerAccountNumber) {
                return;
            }

            let wishlistId: number;

            if (!wishlist) {
                const newList = await createCommerceListAsync(
                    { callerContext: context.actionContext },
                    {
                        CustomerId: customerAccountNumber,
                        Name: WISHLIST_NAME,
                        CommerceListTypeValue: WISHLIST_COMMERCE_LIST_TYPE_VALUE,
                        Id: NEW_COMMERCE_LIST_ID
                    }
                );
                wishlistId = newList.Id;
            } else {
                wishlistId = wishlist.Id;
            }

            const updatedList = await addLinesAsync({ callerContext: context.actionContext }, wishlistId, [
                buildWishlistLine(args.variantRecordId, customerAccountNumber, wishlistId)
            ]);

            context.actionContext.update(createGetByCustomerInput({}, null), [updatedList]);
            setWishlist(updatedList);
            setAddToWishlistError(false);

            if (args.concatenatedId) {
                datalayerService.pushAddToWishlist({
                    item_id: args.concatenatedId,
                    item_name: args.variantDisplayName,
                    discount: args.discount,
                    style_id: args.itemId,
                    index: 0,
                    item_brand: args.brand,
                    item_variant: args.color,
                    price: args.price,
                    quantity: 1,
                    size: args.size
                });
                trackProductEngagement(args.relewiseProductId, args.relewiseVariantId, true);
            }
        },
        [context.request.user.customerAccountNumber, context.actionContext, wishlist, datalayerService, trackProductEngagement]
    );

    useEffect(() => {
        if (typeof window === 'undefined' || !context.request.user.isAuthenticated || !isWishlistReady) {
            return;
        }

        const raw = window.sessionStorage.getItem(PENDING_WISHLIST_ITEM_KEY);
        if (!raw) {
            return;
        }

        let pendingArgs: WishlistItemArgs;
        try {
            pendingArgs = JSON.parse(raw) as WishlistItemArgs;
        } catch {
            logWishlistError(`Failed to add item to wishlist from sessionStorage: Could not parse JSON: ${raw}`);
            window.sessionStorage.removeItem(PENDING_WISHLIST_ITEM_KEY);
            return;
        }

        if (isProductInWishlist(pendingArgs.variantRecordId)) {
            window.sessionStorage.removeItem(PENDING_WISHLIST_ITEM_KEY);
            return;
        }

        addToWishlist(pendingArgs)
            .catch(error => {
                setAddToWishlistError(true);
                logWishlistError(
                    `Failed to add item to wishlist from sessionStorage: ${error instanceof Error ? error.message : String(error)}`
                );
            })
            .then(() => {
                window.sessionStorage.removeItem(PENDING_WISHLIST_ITEM_KEY);
            });
    }, [isWishlistReady, addToWishlist, isProductInWishlist, logWishlistError, context.request.user.isAuthenticated]);

    const removeFromWishlist = useCallback(
        async (args: WishlistItemArgs) => {
            if (!wishlist || args.variantRecordId === undefined) {
                return;
            }

            const wishlistLine = wishlist.CommerceListLines?.find(line => line.ProductId === args.variantRecordId);
            if (!wishlistLine) {
                return;
            }

            const updatedList = await removeLinesAsync({ callerContext: context.actionContext }, wishlist.Id, [wishlistLine]);
            context.actionContext.update(createGetByCustomerInput({}, null), [updatedList]);
            setWishlist(updatedList);
            trackProductEngagement(args.relewiseProductId, args.relewiseVariantId, false);
        },
        [context.actionContext, trackProductEngagement, wishlist]
    );

    const toggleWishlist = useCallback(
        async (args: WishlistItemArgs) => {
            if (isLoading || args.variantRecordId === undefined) {
                return;
            }

            if (!context.request.user.isAuthenticated && context.request.user.signInUrl && typeof window !== 'undefined') {
                window.sessionStorage.setItem(PENDING_WISHLIST_ITEM_KEY, JSON.stringify(args));
                window.location.assign(context.request.user.signInUrl);
                return;
            }

            setIsLoading(true);
            const isInWishlist = isProductInWishlist(args.variantRecordId);
            try {
                if (isInWishlist) {
                    await removeFromWishlist(args);
                } else {
                    await addToWishlist(args);
                }
            } catch (error) {
                // Only set the error when adding to wishlist
                const errorMessage = error instanceof Error ? error.message : String(error);
                if (!isInWishlist) {
                    setAddToWishlistError(true);
                    logWishlistError(`Failed to add item to wishlist: ${errorMessage}`);
                } else {
                    logWishlistError(`Failed to remove item from wishlist: ${errorMessage}`);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [isLoading, isProductInWishlist, context, addToWishlist, removeFromWishlist, logWishlistError]
    );

    return { isProductInWishlist, isLoading, addToWishlistError, toggleWishlist, isWishlistReady };
};
